import { Check, Package, SlidersHorizontal, Truck, UserRound } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Badge, FormSection, Select, SettingsGroup, Switch, Textarea } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import type { ComponentProps, ReactNode } from 'react';
import { useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import type { Geography } from '../geography/useGeoAreas.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  lineOf,
  newOrderFormSchema,
  NO_SOURCE,
  ORDER_FORM_LIMITS,
  previewOf,
  readinessOf,
  unitsOf,
  type NewOrderFormValues,
  type SaleableVariant,
} from './new-order-form.ts';
import { companyOptionsOf, MAIN_COMPANY, type Companies } from '../companies.ts';
import { ORDER_SOURCES } from './orders.ts';
import styles from './NewOrder.module.css';
import { VariantPicker } from './VariantPicker.tsx';
import { DeliveryFields } from './DeliveryFields.tsx';
import { OrderLines } from './OrderLines.tsx';
import { OrderSummary } from './OrderSummary.tsx';

export type Depot = { readonly id: string; readonly name: string; readonly isDefault: boolean };

export type OrderFormState = UseFormReturn<NewOrderFormValues>;

export const useOrderForm = (defaultValues: NewOrderFormValues): OrderFormState =>
  useForm<NewOrderFormValues>({
    resolver: zodResolver(newOrderFormSchema),
    defaultValues,
    mode: 'onBlur',
  });

export type OrderFormProps = {
  api: TypedClient;
  form: OrderFormState;
  currency: string;
  geography: Geography;
  depots: readonly Depot[];
  companies: Companies;
  customer: ReactNode;
  customerChosen: boolean;
  submitLabel: string;
  submitting: boolean;
  errorMessage: string | null;
  secondaryAction?: ReactNode;
  onSubmit: NonNullable<ComponentProps<'form'>['onSubmit']>;
};

const SectionComplete = ({ complete }: { complete: boolean }) => {
  const { t } = usePageContext();
  if (!complete) return null;
  return (
    <Badge tone="success">
      <Check size={14} aria-hidden="true" />
      {t('newOrder.sectionComplete')}
    </Badge>
  );
};

export const OrderForm = ({
  api,
  form,
  currency,
  geography,
  depots,
  companies,
  customer,
  customerChosen,
  submitLabel,
  submitting,
  errorMessage,
  secondaryAction,
  onSubmit,
}: OrderFormProps) => {
  const { t } = usePageContext();
  const lines = useFieldArray({ control: form.control, name: 'lines' });
  const [locationId, company, watchedLines, shippingFee, address, governorate] = useWatch({
    control: form.control,
    name: ['locationId', 'company', 'lines', 'shippingFee', 'address', 'governorate'],
  });
  const readiness = readinessOf({ lines: watchedLines, address, governorate, customerChosen });
  const addOrCountLine = (variant: SaleableVariant) => {
    const index = watchedLines.findIndex((line) => line.variantId === variant.variantId);
    if (index === -1) lines.append(lineOf(variant), { shouldFocus: false });
    else
      form.setValue(
        `lines.${String(index)}.quantity` as `lines.${number}.quantity`,
        (watchedLines[index]?.quantity ?? 0) + 1,
      );
  };

  return (
    <form noValidate className={styles['page']} onSubmit={onSubmit}>
      <div className={styles['layout']}>
        <div className={styles['main']}>
          <FormSection
            title={t('newOrder.itemsSection')}
            description={t('newOrder.itemsHint')}
            icon={<Package size={18} />}
            status={<SectionComplete complete={readiness.items} />}
          >
            {companies.subsidiaries.length === 0 ? null : (
              <Select
                label={t('companies.label')}
                required
                helperText={t('companies.orderHelp')}
                options={companyOptionsOf(companies, form.formState.defaultValues?.company ?? MAIN_COMPANY)}
                {...form.register('company')}
              />
            )}
            <VariantPicker
              api={api}
              company={company}
              locationId={locationId}
              currency={currency}
              addedIds={new Set(watchedLines.map((line) => line.variantId))}
              onAdd={addOrCountLine}
            />
            <OrderLines
              form={form}
              fields={lines.fields}
              watchedLines={watchedLines}
              currency={currency}
              onRemove={lines.remove}
            />
          </FormSection>
          <FormSection
            title={t('newOrder.customerSection')}
            description={t('newOrder.customerHint')}
            icon={<UserRound size={18} />}
            status={<SectionComplete complete={readiness.customer} />}
          >
            {customer}
          </FormSection>
          <FormSection
            title={t('newOrder.deliverySection')}
            description={t('newOrder.deliveryHint')}
            icon={<Truck size={18} />}
            status={<SectionComplete complete={readiness.delivery} />}
          >
            <DeliveryFields
              form={form}
              geography={geography}
              depot={
                <Select
                  label={t('orders.depot')}
                  required
                  disabled={depots.length === 1}
                  options={depots.map((depot) => ({ value: depot.id, label: depot.name }))}
                  {...form.register('locationId')}
                />
              }
            />
          </FormSection>
          <FormSection
            title={t('newOrder.optionsSection')}
            description={t('newOrder.optionsHint')}
            icon={<SlidersHorizontal size={18} />}
          >
            <Select
              label={t('orders.source')}
              helperText={t('orders.sourceHelp')}
              options={[
                { value: NO_SOURCE, label: t('orders.source_none') },
                ...ORDER_SOURCES.map((source) => ({ value: source, label: t(`orders.source_${source}`) })),
              ]}
              {...form.register('source')}
            />
            <SettingsGroup>
              <Switch
                label={t('orders.exchange')}
                helperText={t('orders.exchangeHelp')}
                {...form.register('isExchange')}
              />
            </SettingsGroup>
            <Textarea
              label={t('orders.note')}
              maxLength={ORDER_FORM_LIMITS.note}
              {...form.register('note')}
            />
          </FormSection>
        </div>
        <OrderSummary
          preview={previewOf(watchedLines, shippingFee)}
          units={unitsOf(watchedLines)}
          shippingFee={shippingFee}
          currency={currency}
          readiness={readiness}
          submitLabel={submitLabel}
          submitting={submitting}
          errorMessage={errorMessage}
          secondaryAction={secondaryAction}
        />
      </div>
    </form>
  );
};
